import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { professionalLabel } from "@/lib/professional";
import { usePremium } from "@/components/Paywall";
import { enablePush } from "@/lib/push";
import { UserRound, Crown, RotateCcw, Bell, LogOut, ShieldCheck } from "lucide-react";

export default function Perfil() {
  const { user, setUser, logout } = useAuth();
  const premium = usePremium();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const isPatient = user?.role === "patient";

  useEffect(() => {
    api.get("/me/subscription").then((r) => setSub(r.data)).catch(() => {});
    if (isPatient) api.get("/me/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [isPatient]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email });
      setUser(data);
      toast.success("Perfil atualizado.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/subscription/restore");
      setSub(data);
      toast.success(data.status === "premium" ? "Premium restaurado." : "Nenhuma compra ativa encontrada.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const push = async () => {
    try { await enablePush(); toast.success("Notificações ativadas neste dispositivo."); }
    catch (e) { toast.error(e.message || "Não foi possível ativar as notificações."); }
  };

  const isPremium = sub?.status === "premium";
  const roleLabel = { patient: "Paciente", professional: professionalLabel(user?.professional_type), org_admin: "Administração" }[user?.role];

  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fade-up" data-testid="perfil-page">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[#0F766E] text-white grid place-items-center font-semibold text-xl">{user?.name?.[0]?.toUpperCase()}</div>
        <div><h1 className="font-heading text-2xl font-bold text-[#1F3B4D]">{user?.name}</h1><div className="text-sm text-[#94A3B8]">{roleLabel}</div></div>
      </div>

      <Card className="rounded-2xl p-4 border-[#E0F2FE] space-y-3">
        <div className="text-sm font-semibold text-[#1F3B4D] flex items-center gap-2"><UserRound size={16} className="text-[#0F766E]" /> Dados pessoais</div>
        <div className="space-y-1"><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="perfil-name-input" className="rounded-xl border-[#E0F2FE]" /></div>
        <div className="space-y-1"><Label>E-mail</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="perfil-email-input" className="rounded-xl border-[#E0F2FE]" /></div>
        <Button onClick={save} disabled={saving} data-testid="perfil-save-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">{saving ? "Salvando…" : "Salvar"}</Button>
      </Card>

      {isPatient && summary?.professional && (
        <Card className="rounded-2xl p-4 border-[#E0F2FE]">
          <div className="text-sm font-semibold text-[#1F3B4D] mb-1">Profissional responsável</div>
          <div className="text-sm text-[#1F3B4D]" data-testid="perfil-responsavel">{professionalLabel(summary.professional.professional_type)}: {summary.professional.name}</div>
        </Card>
      )}

      <Card className="rounded-2xl p-4 border-[#E0F2FE] space-y-2" data-testid="perfil-subscription">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1F3B4D]"><Crown size={16} className="text-[#F59E0B]" /> Assinatura</div>
          <Badge className={isPremium ? "bg-[#0F766E] text-white border-0" : "bg-[#E0F2FE] text-[#0F766E] border-0"} data-testid="perfil-plan-badge">{isPremium ? "PREMIUM" : "FREE"}</Badge>
        </div>
        <div className="text-xs text-[#94A3B8]">Plano atual: <span className="font-semibold text-[#1F3B4D]">{isPremium ? "Premium ativo" : "Free"}</span></div>
        <div className="flex gap-2 flex-wrap">
          {!isPremium && <Button size="sm" onClick={() => premium?.openPaywall()} data-testid="perfil-know-premium" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-1"><Crown size={14} /> Conhecer Premium</Button>}
          <Button size="sm" variant="outline" onClick={restore} disabled={busy} data-testid="perfil-restore" className="rounded-full border-[#E0F2FE] gap-1"><RotateCcw size={14} /> Restaurar compras</Button>
        </div>
      </Card>

      <Card className="rounded-2xl p-4 border-[#E0F2FE] space-y-2">
        <div className="text-sm font-semibold text-[#1F3B4D] flex items-center gap-2"><ShieldCheck size={16} className="text-[#0F766E]" /> Conta, notificações e segurança</div>
        <Button variant="outline" onClick={push} data-testid="perfil-push" className="w-full rounded-xl border-[#E0F2FE] gap-2 justify-start"><Bell size={15} /> Ativar notificações neste dispositivo</Button>
        <Button variant="outline" onClick={logout} data-testid="perfil-logout" className="w-full rounded-xl border-[#E0F2FE] gap-2 justify-start text-[#EF4444]"><LogOut size={15} /> Sair</Button>
      </Card>
    </div>
  );
}
